import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { fnCheckDuplicateSn } from "@shared/utils/InvUtils";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MPR_REG_002_POP_001 from "@menu/MTL/SN_MTL_MPR_REG_002/SN_MTL_MPR_REG_002_POP_001";
import SN_MTL_MPR_REG_002_POP_002 from "@menu/MTL/SN_MTL_MPR_REG_002/SN_MTL_MPR_REG_002_POP_002";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MPR_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  let isScan = useRef<Boolean>(false); // 바코드 스캔한 건인지 여부
  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "1", "3", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mpr_reg_002_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      no_io: focusedRowNodeH.data?.no_io,
    });

    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtioSnChanges = getGridCurrent("grid3").getChange();
      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0 &&
        qtioSnChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      let hasInvalid = false;
      let hasInvalid2 = false;

      getGridCurrent("grid2").forEachNode((node: RowNode) => {
        if (node.data.state === "insert") {
          if (node.data.qt_req > 0 && node.data.qt > node.data.qt_req) {
            hasInvalid = true;
          }
          if (node.data.fg_unit === "LOT") {
            if (converter.getString(node.data.no_lot) === "") {
              hasInvalid2 = true;
            }
          }
        }
      });

      if (hasInvalid) {
        await showDialog("alter", "요청수량을 넘어선 건이 있습니다.");
        return false;
      }
      if (hasInvalid2) {
        await showDialog("alter", "LOT관리 품목의 LOT가 입력되지 않았습니다.");
        return false;
      }

      // 1. insert 배열에서 cd_item과 no_sn 추출 및 내부 중복 체크
      const inserts = qtioSnChanges.insert ?? [];
      const localDuplicates: string[] = []; // 내부 중복된 시리얼 번호를 담을 배열
      const seen = new Set<string>(); // 비교를 위한 임시 셋

      const snCheckList = inserts.map((item: any) => {
        const identifier = `${item.cd_item}_${item.no_sn.toLowerCase()}`; // 품목과 시리얼의 조합 키 생성

        if (item.no_sn === "") {
          hasInvalid = true;
        }
        if (seen.has(identifier)) {
          // 이미 셋에 존재한다면 화면에 입력된 데이터들끼리 중복된 것임
          if (!localDuplicates.includes(item.no_sn.toLowerCase())) {
            localDuplicates.push(item.no_sn.toLowerCase());
          }
        } else {
          seen.add(identifier);
        }

        return {
          cd_item: item.cd_item,
          no_sn: item.no_sn,
          cd_biz: item.cd_biz,
        };
      });

      if (hasInvalid) {
        await showDialog("alter", "시리얼 번호가 입력되지 않은 건이 있습니다.");
        return false;
      }

      if (localDuplicates.length > 0) {
        await showDialog(
          "alter",
          `입력된 목록 중 중복된 시리얼 번호가 있습니다.\n${localDuplicates.join(", ")}`
        );
        return false;
      }

      // 2. 가공된 배열을 함수로 전달
      if (snCheckList.length > 0) {
        const duplicateSns = await fnCheckDuplicateSn(snCheckList);

        if (duplicateSns && duplicateSns.length > 0) {
          await showDialog(
            "alter",
            `이미 등록된 시리얼 번호가 포함되어 있습니다.\n${duplicateSns.join(", ")}`
          );
          return false;
        }
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 2,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid3"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtioSnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_sn: qtioSnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MPR_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (focusedRowNodeH) {
      return {
        cd_firm: focusedRowNodeH.data.cd_firm,
        cd_biz: focusedRowNodeH.data.cd_biz,
        no_io: focusedRowNodeH.data.no_io,
      };
    } else {
      return;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }

      // 필터초기화
      getGridCurrent("grid3").applyFilters({
        no_io: "",
        no_line: "",
      });

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        fg_trns: "1",
        rt_exch: 1,
        cd_exch: "000",
        tp_um: "001",
        tp_slip: "3",
        fg_slip: "1",
        no_io: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });

          getGridCurrent("grid4").forEachNode((node: RowNode) => {
            if (node.data.no_io === rowData.no_io) {
              node.setDataValue(colId, rowData.cd_io, "noEvent");
              node.setDataValue("fg_io", ioType.fg_io, "noEvent");
              node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            }
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io, "noEvent");
        });
        break;
      case "cd_pjt":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_pjt, "noEvent");
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid3").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    // searchPanel에서 선택된 값 전달
    const selectedFormData = searchRef.current.getFormdata();
    const selectedData = {
      cd_biz: selectedFormData.cd_biz,
      dt_from: selectedFormData.dt_from,
      dt_to: selectedFormData.dt_to,
    };

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") {
        focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
      }
    });
    getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    switch (id) {
      case "btn_POP1":
        const component = (
          <SN_MTL_MPR_REG_002_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "실적적용",
          children: component,
          validation: true,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            switch (key) {
              case "cd_io":
                focusedRowNodeH.setDataValue("cd_io", headerResult[0][key]);
                break;
            }
          }
        }
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);

        headerResult.map((result: any) => {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              switch (key) {
                case "no_run":
                  addedNode.setDataValue("no_req", result[key], "noEvent");
                  break;
                case "no_wo":
                  addedNode.setDataValue("no_order", result[key], "noEvent");
                  break;
                case "no_line_wo":
                  addedNode.setDataValue(
                    "no_line_order",
                    result[key],
                    "noEvent"
                  );
                  break;
                default:
                  addedNode.setDataValue(key, result[key], "noEvent");
                  break;
              }
            }

            addedNode.setDataValue("yn_req", "Y", "noEvent");

            switch (result.fg_unit) {
              case "LOT":
                {
                  lineResult.map((resultBarcode: any) => {
                    if (result.no_run === resultBarcode.no_run) {
                      if (
                        addedNode.data.no_lot === "" ||
                        addedNode.data.no_lot === null ||
                        addedNode.data.no_lot === undefined
                      ) {
                        addedNode.setDataValue(
                          "qt_good",
                          resultBarcode.qt_w,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "qt_bad",
                          resultBarcode.qt_b,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "qt",
                          resultBarcode.qt_remain,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "qt_req",
                          resultBarcode.qt_remain,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "no_line_req",
                          resultBarcode.no_line,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "no_lot",
                          resultBarcode.no_barcode,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "no_ct",
                          resultBarcode.no_ct,
                          "noEvent"
                        );
                      } else {
                        const addedNodeNew = onAddRow();
                        if (addedNodeNew) {
                          addedNode.setDataValue(
                            "no_lot",
                            resultBarcode.no_barcode,
                            "noEvent"
                          );
                          addedNode.setDataValue(
                            "no_ct",
                            resultBarcode.no_ct,
                            "noEvent"
                          );
                          for (const key in result) {
                            switch (key) {
                              case "no_run":
                                addedNodeNew.setDataValue(
                                  "no_req",
                                  result.no_run,
                                  "noEvent"
                                );
                                break;
                              case "no_wo":
                                addedNodeNew.setDataValue(
                                  "no_order",
                                  result.no_wo,
                                  "noEvent"
                                );
                                break;
                              case "no_line_wo":
                                addedNodeNew.setDataValue(
                                  "no_line_order",
                                  result.no_line_wo,
                                  "noEvent"
                                );
                                break;
                              default:
                                addedNodeNew.setDataValue(
                                  key,
                                  result[key],
                                  "noEvent"
                                );
                                break;
                            }
                          }
                          addedNodeNew.setDataValue(
                            "qt_good",
                            resultBarcode.qt_w,
                            "noEvent"
                          );
                          addedNodeNew.setDataValue(
                            "qt_bad",
                            resultBarcode.qt_b,
                            "noEvent"
                          );
                          addedNodeNew.setDataValue(
                            "qt",
                            resultBarcode.qt_remain,
                            "noEvent"
                          );
                          addedNodeNew.setDataValue(
                            "qt_req",
                            resultBarcode.qt_remain,
                            "noEvent"
                          );
                        }
                      }
                    }
                  });
                }
                break;
              case "SN":
                {
                  addedNode.setDataValue("qt", 0, "noEvent");

                  lineResult.map((resultBarcode: any) => {
                    if (result.no_run === resultBarcode.no_run) {
                      addedNode.setDataValue(
                        "no_line_req",
                        resultBarcode.no_line,
                        "noEvent"
                      );
                      addedNode.setDataValue(
                        "qt_req",
                        addedNode.data.qt_req + resultBarcode.qt_remain,
                        "noEvent"
                      );
                      if (
                        converter.getString(resultBarcode.no_barcode) !== ""
                      ) {
                        addedNode.setDataValue(
                          "qt_good",
                          addedNode.data.qt_good + resultBarcode.qt_w,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "qt_bad",
                          addedNode.data.qt_bad + resultBarcode.qt_b,
                          "noEvent"
                        );
                        addedNode.setDataValue(
                          "qt",
                          addedNode.data.qt + 1,
                          "noEvent"
                        );

                        const addedNodeD = getGridCurrent("grid3").addRow();
                        if (addedNodeD) {
                          addedNodeD.setDataValue(
                            "no_io",
                            addedNode.data.no_io,
                            "noEvent"
                          );
                          addedNodeD.setDataValue(
                            "no_line",
                            addedNode.data.no_line,
                            "noEvent"
                          );
                          addedNodeD.setDataValue(
                            "cd_item",
                            addedNode.data.cd_item,
                            "noEvent"
                          );
                          addedNodeD.setDataValue(
                            "no_sn",
                            resultBarcode.no_barcode,
                            "noEvent"
                          );
                          addedNodeD.setDataValue(
                            "no_ct",
                            resultBarcode.no_ct,
                            "noEvent"
                          );
                        }
                      }
                    }
                  });
                }
                break;
              case "QT":
                lineResult.map((resultBarcode: any) => {
                  if (result.no_run === resultBarcode.no_run) {
                    addedNode.setDataValue(
                      "no_line_req",
                      resultBarcode.no_line,
                      "noEvent"
                    );
                    addedNode.setDataValue("qt_good", result.qt_w, "noEvent");
                    addedNode.setDataValue("qt_bad", result.qt_b, "noEvent");
                    addedNode.setDataValue("qt", result.qt_remain, "noEvent");
                    addedNode.setDataValue(
                      "qt_req",
                      result.qt_remain,
                      "noEvent"
                    );
                  }
                });
                break;
            }
            getGridCurrent("grid2").applyBlock(addedNode);
          }
        });

        break;
      case "btn_POP2":
        {
          const component = (
            <SN_MTL_MPR_REG_002_POP_002 selectedData={selectedData} />
          );
          const result = await showResizablePopupModal({
            title: "의뢰적용",
            children: component,
          });

          if (!result || result === "close") return;

          const headerResult = result[0];
          const lineResult = result[1];

          // 요청도움창 Header 결과 적용
          if (focusedRowNodeH && headerResult[0]) {
            for (const key in headerResult[0]) {
              switch (key) {
                case "cd_io":
                  focusedRowNodeH.setDataValue(key, headerResult[0][key]);
                  break;
              }
            }
          }

          // 요청도움창 Line 결과 적용
          lineResult.map((result: any) => {
            if (headerResult[0]) {
              const addedNode = onAddRow();
              if (addedNode) {
                for (const key in result) {
                  switch (key) {
                    case "no_line":
                      addedNode.setDataValue(
                        "no_line_req",
                        result.no_line,
                        "noEvent"
                      );
                      break;
                    default:
                      addedNode.setDataValue(key, result[key], "noEvent");
                      break;
                  }
                }
                addedNode.setDataValue("yn_req", "Y", "noEvent");

                if (result.fg_unit !== "SN") {
                  addedNode.setDataValue(
                    "qt_good",
                    result.qt_remain,
                    "noEvent"
                  );
                  addedNode.setDataValue("qt", result.qt_remain, "noEvent");
                } else {
                  addedNode.setDataValue("qt", 0, "noEvent");
                }

                addedNode.setDataValue("qt_req", result.qt_remain, "noEvent");

                getGridCurrent("grid2").applyBlock(addedNode);
              }
            }
          });
          getGridCurrent("grid1").applyBlock(focusedRowNodeH);
        }
        break;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      let cd_wh_last = "";
      let nm_wh_last = "";
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount !== 0) {
        const lastNode = getGridCurrent("grid2").getRowNodeAtIndex(
          rowCount - 1
        );

        if (lastNode != null) {
          cd_wh_last = lastNode.data.cd_wh;
          nm_wh_last = lastNode.data.nm_wh;
        }
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_map: focusedRowNodeH.data?.fg_map,
        fg_vat: "11",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        qt: 0,
        qt_good: 0,
        qt_bad: 0,
        cd_wh: cd_wh_last,
        nm_wh: nm_wh_last,
        yn_req: "N",
        qt_req: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_wh") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_wh) return;

      let apply = false;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (
            node.data.cd_wh === "" ||
            node.data.cd_wh === null ||
            node.data.cd_wh === undefined
          ) {
            node.setDataValue("cd_wh", rowData.cd_wh);
            node.setDataValue("nm_wh", rowData.nm_wh);
          }
        });
      }
    }

    switch (colId) {
      case "cd_item": // 품목코드바뀌면 초기화
        if (
          rowData.cd_item !== "" ||
          rowData.cd_item !== null ||
          rowData.cd_item !== undefined
        ) {
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          rowData.no_lot = "";
          getGridCurrent("grid3").deleteAllRows();
        }
        break;
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
        break;
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region GridD Events
  const onBeforeAddRowD = (): any => {
    try {
      const focusedRowNode = getGridCurrent("grid2").focusedRowNode();
      if (!focusedRowNode) return;

      const updatedRow = {
        cd_firm: focusedRowNode.data?.cd_firm,
        cd_biz: focusedRowNode.data?.cd_biz,
        no_io: focusedRowNode.data?.no_io,
        no_line: focusedRowNode.data?.no_line,
        cd_item: focusedRowNode.data?.cd_item,
        no_sn: isScan.current == true ? txtBarcodeRef.current.getValue() : "",
      };

      focusedRowNode.setDataValue(
        "qt_good",
        Number(focusedRowNode.data?.qt_good) + 1
      );

      isScan.current = false;

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onRowDataUpdatedSn = () => {
    const focusedRowNode = getGridCurrent("grid3").focusedRowNode();

    if (focusedRowNode) {
      if (
        focusedRowNode.data.no_sn === "" ||
        focusedRowNode.data.no_sn === null ||
        focusedRowNode.data.no_sn === undefined
      ) {
        return;
      }
    }
    txtBarcodeRef.current.focus();
  };

  const onGridDBtnsClick = async (id: string) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (
      focusedRowNodeL.data.fg_unit === "SN" &&
      focusedRowNodeL.data.cd_item !== "" &&
      focusedRowNodeL.data.cd_item !== null &&
      focusedRowNodeL.data.cd_item !== undefined
    ) {
      switch (id) {
        case "addRow":
          if (focusedRowNodeL.data.state !== "insert") {
            showDialog("alter", "신규 행에서만 입력 가능합니다.");
            return;
          }

          let hasInvalid = false;

          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.no_sn === "") {
              hasInvalid = true;
            }
          });
          if (hasInvalid) {
            return;
          } else {
            return getGridCurrent("grid3").addRow();
          }
        case "deleteRow":
          if (getGridCurrent("grid3").getDisplayedRowCount() < 1) {
            return;
          }
          getGridCurrent("grid3").deleteSelectedRows();

          focusedRowNodeL.setDataValue(
            "qt_good",
            Number(focusedRowNodeL.data?.qt_good) - 1
          );
          break;
      }
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const barcodeText = e;

    if (
      barcodeText === "" ||
      barcodeText === null ||
      barcodeText === undefined
    ) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 입력 가능합니다.");
      return;
    }

    if (
      focusedRowNodeL.data.qt_req > 0 &&
      focusedRowNodeL.data.qt_req == focusedRowNodeL.data.qt
    ) {
      showDialog("alter", "요청수량만큼 다 등록되었습니다.");
      return;
    }

    if (
      focusedRowNodeL.data.fg_unit === "SN" &&
      focusedRowNodeL.data.cd_item !== "" &&
      focusedRowNodeL.data.cd_item !== null &&
      focusedRowNodeL.data.cd_item !== undefined
    ) {
      let hasInvalid = false;

      getGridCurrent("grid3").forEachNode((node: any) => {
        if (barcodeText.toLowerCase() === node.data.no_sn.toLowerCase()) {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        await showDialog("alter", "이미 입력한 시리얼 번호입니다.");
        txtBarcodeRef.current.focus();
        return;
      }

      const params = {
        cd_biz: focusedRowNodeL.data.cd_biz,
        cd_item: focusedRowNodeL.data.cd_item,
        no_sn: barcodeText,
      };

      const data = await fnCheckDuplicateSn(params);

      if (data && data.length > 0) {
        await showDialog(
          "alter",
          `이미 등록된 시리얼 번호입니다: ${barcodeText}`
        );
        txtBarcodeRef.current.focus();
        return;
      }

      isScan.current = true;
      getGridCurrent("grid3").addRow();
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBeforeAddRow: onBeforeAddRowD,
          onRowDataUpdated: onRowDataUpdatedSn,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "입고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"입고의뢰적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP2");
                  }}
                />
                <SnButton
                  text={"실적적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="75%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                filter={{
                  refs: [gridRefs.current["grid3"]],
                  columns: ["no_io", "no_line"],
                }}
                events={grids.grid2.events}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0">
              <SnGrid
                ref={gridRefs.current["grid3"]} // Grid Name
                columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                rowData={grids.grid3.dataSource} // Datasource
                etcProps={{ rowSelection: "single", title: "SN" }}
                events={grids.grid3.events}
                enablePasteOnlyAdd={true}
                header={
                  <div>
                    <SnBarcodeBox
                      onEnter={onBarcodeEnter}
                      ref={txtBarcodeRef}
                      inputRef={useRef(null)}
                    />
                  </div>
                }
              />
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MPR_REG_002;
